import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useAboutMe } from "@/hooks/usePortfolio";
import { getImageUrl } from "@/api/client";
import { SectionTitle } from "@/components/SectionTitle";

export const About = () => {
  const [expanded, setExpanded] = useState(false);
  const { data: aboutMe, isLoading, error } = useAboutMe();

  if (isLoading) return <div>Loading...</div>;
  if (error || !aboutMe) return null;

  const paragraphs = (aboutMe.detailedDescription || "").split("\n\n").filter(Boolean);
  const preview = paragraphs.slice(0, 3);
  const rest = paragraphs.slice(3);

  return (
    <section id="about" className="section-pad relative" data-testid="about-section">
      <div className="container-x">
        <SectionTitle eyebrow="About" title={<>The story behind <span className="italic gold-text">two decades</span> of business</>}
          subtitle={aboutMe.heading || "A glimpse into the experience, philosophy and approach that has shaped a 21-year journey across enterprise sales, telecom and unified communications."} />

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <div className="relative aspect-[3/4] max-w-md">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-tr from-[hsl(var(--navy))]/20 to-[hsl(var(--gold))]/20 blur-2xl" />
              <div className="relative h-full rounded-3xl overflow-hidden glass p-2">
                <img src={getImageUrl(aboutMe.profileImageUrl)} alt="Portrait" className="w-full h-full object-cover rounded-[1.4rem]" />
              </div>
              <div className="absolute -bottom-6 right-0 sm:-right-4 md:-right-8 glass-strong rounded-2xl p-5 max-w-[240px]">
                <div className="font-serif text-4xl gold-text">21+</div>
                <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mt-1">Years in Sales & BD</div>
              </div>
            </div>
          </motion.div>

          <div className="lg:col-span-7">
            <div className="prose-luxury">
              {preview.map((p, i) => (
                <motion.p
                  key={i}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                >
                  {p}
                </motion.p>
              ))}
              <AnimatePresence initial={false}>
                {expanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    {rest.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {rest.length > 0 && (
              <button
                data-testid="about-read-more"
                onClick={() => setExpanded((e) => !e)}
                className="mt-6 inline-flex items-center gap-2 text-sm font-medium tracking-wide text-[hsl(var(--gold-deep))] dark:text-[hsl(var(--gold-light))] hover:gap-3 transition-all"
              >
                {expanded ? "Read Less" : "Read More"}
                {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
